import React, { useEffect, useRef, useState } from 'react';

// progress-state-handoff.md, "Iteration 1": one label, the seconds, and a second line from 10s.
export const PROGRESS_LABEL = 'Preparing your answer';
export const LINE_USUAL = 'Usually takes less than a minute.';
export const LINE_LONG = 'Taking longer than usual.';
const LONG_RUN_MS = 10000;
const PAST_MINUTE_MS = 60000;

/** "8s", "59s", "1m 5s": the seconds read as minutes past a minute, as elsewhere in ResAI. */
export function formatElapsed(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

export function secondLine(ms) {
  if (ms >= PAST_MINUTE_MS) return LINE_LONG;
  if (ms >= LONG_RUN_MS) return LINE_USUAL;
  return null;
}

/**
 * PRD-1624: what Ask ResAI shows while an answer is prepared, in place of "Thinking".
 * `startedAt` (ms) is when the question was sent, or the run's start when returning to a
 * thread mid-answer, so the seconds are right either way. Announces the start, the 10s
 * line and the 1m line once each through `announce`; the seconds are never announced.
 */
function ProgressRow({ startedAt, announce }) {
  const [now, setNow] = useState(() => Date.now());
  // With no known start (a run seen only by polling), count from when the row appeared.
  const shownAt = useRef(Date.now());
  const said = useRef({});

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  const elapsed = now - (startedAt || shownAt.current);
  const line = secondLine(elapsed);

  useEffect(() => {
    if (!announce) return;
    if (!said.current.start) {
      said.current.start = true;
      announce(PROGRESS_LABEL);
    }
    if (line === LINE_USUAL && !said.current.usual) {
      said.current.usual = true;
      announce(`${PROGRESS_LABEL}. ${LINE_USUAL}`);
    }
    if (line === LINE_LONG && !said.current.long) {
      said.current.long = true;
      announce('Taking longer than usual');
    }
  }, [line, announce]);

  return (
    <div className="message assistant iter1-progress" data-testid="iter1-progress">
      <div className="iter1-progress-row">
        <span className="thinking-dots iter1-progress-dots" aria-hidden="true">
          <span></span><span></span><span></span>
        </span>
        <span className="iter1-progress-line">
          <span className="iter1-progress-time" aria-hidden="true">{formatElapsed(elapsed)}</span>
          {' · '}
          <span className="iter1-progress-label">{PROGRESS_LABEL}</span>
        </span>
      </div>
      {line && <p className="iter1-progress-more">{line}</p>}
    </div>
  );
}

export default ProgressRow;
